import React from "react";

import { slugify } from "@covertree/utils";
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  Switch,
  Typography,
} from "@mui/material";
import { snakeCase } from "lodash";

import { CardMedia } from "../../cardMedia";
import { SwitchWithLabel } from "../../switch";
import { Tooltip } from "../../content";
import { ToggleCardProps } from "./types";

export const MobileToggleCard = ({
  actions,
  actionText,
  actionSubText,
  checked,
  content,
  customizable,
  image,
  onToggle,
  subheader,
  switchLabels,
  subheaderVariant = "grey",
  title,
  tooltip,
  disabled,
  fontSize,
}: ToggleCardProps): JSX.Element => {
  const id = snakeCase(title);

  return (
    <Card
      role="option"
      aria-selected={checked}
      data-testid={`${slugify(title)}-card`}
      aria-labelledby={id}
      sx={(theme) => ({
        display: "flex",
        flexDirection: "row",
        borderWidth: 1,
        "&:hover": {
          boxShadow: theme.shadows[2],
        },
        padding: theme.spacing(2.875),
        transition: theme.transitions.create(["background-color", "transform"]),
        backgroundColor: theme.palette.common.white,
        ...(checked
          ? {
              borderWidth: 0,
              padding: theme.spacing(3),
              boxShadow: theme.shadows[0],
              backgroundColor: theme.palette.secondary.contrastText,
              "&:hover": {
                boxShadow: theme.shadows[0],
              },
            }
          : {}),
      })}>
      <Box flexGrow={1}>
        <CardHeader
          sx={{ alignItems: "flex-start" }}
          title={
            <Box>
              <Grid
                sx={{
                  ...(image
                    ? {}
                    : {
                        alignItems: "center",
                        flexWrap: "wrap",
                        flexDirection: "row-reverse",
                        justifyContent: "space-between",
                        "& .grid-item-switch": {
                          flex: "0 0 auto",
                          marginRight: 0,
                          maxWidth: "100%",
                          width: "auto",
                        },
                        "& .grid-item-title": {
                          flex: 1,
                        },
                      }),
                }}
                container>
                {image && (
                  <Grid item xs={4}>
                    <CardMedia
                      data-testid={`${slugify(title)}-image`}
                      image={image}
                    />
                  </Grid>
                )}
                <Grid
                  item
                  container
                  className="grid-item-switch"
                  xs={8}
                  alignItems="center"
                  justifyContent="flex-end">
                  <Box
                    display="flex"
                    alignItems="center"
                    color={checked ? "secondary.main" : "text.secondary"}>
                    <Box>
                      {actionText && (
                        <Typography
                          variant="h5"
                          fontSize={fontSize}
                          color={
                            actionSubText && !checked
                              ? "text.primary"
                              : "inherit"
                          }
                          ml={0.5}>
                          {actionText}
                        </Typography>
                      )}

                      {actionSubText && (
                        <Typography
                          variant="subtitle1"
                          fontSize={fontSize}
                          color="inherit"
                          ml={2.5}
                          sx={{ textWrap: "nowrap" }}>
                          {actionSubText}
                        </Typography>
                      )}
                    </Box>
                    {customizable && switchLabels && (
                      <SwitchWithLabel
                        onChange={onToggle}
                        dataTestId={`${slugify(title)}-switch`}
                        value={checked}
                        labels={
                          Array.isArray(switchLabels) ? switchLabels : undefined
                        }
                      />
                    )}
                    {customizable && !switchLabels && (
                      <Switch
                        role="switch"
                        data-testid={`${slugify(title)}-switch`}
                        onClick={onToggle}
                        checked={checked}
                        sx={(theme) => ({ marginLeft: theme.spacing(2) })}
                        disabled={disabled}
                      />
                    )}
                  </Box>
                </Grid>
                <Grid className="grid-item-title" item container xs={12} md={7}>
                  <Box
                    flex={1}
                    display="flex"
                    gap={1}
                    role="heading"
                    id={id}
                    data-testid={`${slugify(title)}-title`}>
                    <Typography
                      variant="h5"
                      color="inherit"
                      fontSize={fontSize}>
                      {title}
                      {tooltip && (
                        <Tooltip
                          tooltip={tooltip}
                          dataTestId={`${slugify(title)}`}
                          placement="bottom"
                          fontSize={fontSize}
                          sx={{
                            verticalAlign: "middle",
                            marginBottom: 0.25,
                            marginLeft: 1,
                          }}
                        />
                      )}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          }
          subheader={subheader}
          subheaderTypographyProps={{
            sx: (theme) => ({
              color:
                subheaderVariant === "black"
                  ? theme.palette.text.primary
                  : theme.palette.text.secondary,
            }),
          }}
        />

        {content && (
          <CardContent
            sx={(theme) => ({ padding: 0, marginTop: theme.spacing(1.25) })}>
            {" "}
            {content}{" "}
          </CardContent>
        )}
        {actions && (
          <CardActions
            sx={(theme) => ({
              width: "100%",
              padding: 0,
              marginTop: theme.spacing(3),
            })}>
            {actions}
          </CardActions>
        )}
      </Box>
    </Card>
  );
};
export default MobileToggleCard;
